import {Grid, Stack, Box, Typography} from '@mui/material'

import * as React from 'react'
import {FormProvider} from '../../common/hook-form'
import {useForm} from 'react-hook-form'
import {useNavigate} from 'react-router-dom'

import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'
import VisibilityIcon from '@mui/icons-material/Visibility'
import LoginIcon from '@mui/icons-material/Login'
import GoogleIcon from '@mui/icons-material/Google'
import PhoneIcon from '@mui/icons-material/Phone'
import EmailIcon from '@mui/icons-material/Email'
import {yupResolver} from '@hookform/resolvers/yup'
import {useSnackbar} from 'notistack'
import {useLoginLogic} from './hooks/useLoginLogic'
import {isProzoPasswordLoginBlocked, PROZO_EMAIL_LOGIN_ERROR} from '../../helpers/UtilityHelper'
import WindowIcon from '@mui/icons-material/Window';

import * as Yup from 'yup'
import {
  ForgotPasswordButton,
  GoogleSignInButton,
  HeaderContainer,
  IconContainer,
  MSALSignInButton,
  PasswordToggleButton,
  StyledIcon,
  StyledLoadingButton,
  StyledTextField,
  WelcomeTitle,
} from './StyledElements'

export default function LoginForm2() {
  const [isLoading, setIsLoading] = React.useState(false)
  const [showPassword, setShowPassword] = React.useState(false)
  const {enqueueSnackbar} = useSnackbar()
  const navigate = useNavigate()
  const environment = process.env.REACT_APP_ENV

  const {handleEmailLogin, handlePhoneLogin, handleGoogleSignIn, handleMSALSignIn} = useLoginLogic()

  const loginValidationSchema = Yup.object().shape({
    username: Yup.string()
      .required('Please enter email or phone number')
      .test('isValidInput', 'Enter valid email or 10 digit phone number', (value) => {
        if (!value) return false
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        const phoneRegex = /^\d{10}$/
        return emailRegex.test(value) || phoneRegex.test(value)
      })
      .test('notProzoEmail', PROZO_EMAIL_LOGIN_ERROR, (value) => !isProzoPasswordLoginBlocked(value)),
    password: Yup.string().when('username', {
      is: (value) => value && value.includes('@'),
      then: () => Yup.string().required('Please enter password'),
      otherwise: () => Yup.string(),
    }),
  })

  const methods = useForm({
    resolver: yupResolver(loginValidationSchema),
    defaultValues: {
      username: '',
      password: '',
    },
  })

  const {handleSubmit, getValues, watch} = methods
  const userNameValue = watch('username')

  const isEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(userNameValue)
  const isPhoneNumber = /^\d{10}$/.test(userNameValue)
  const isProzoEmail = userNameValue.endsWith('@prozo.com')

  const onSubmit = async (reqData) => {
    try {
      setIsLoading(true)
      if (isPhoneNumber) {
        await handlePhoneLogin(reqData.username)
      } else if (isEmail) {
        await handleEmailLogin(reqData)
      }
    } catch (error) {
      enqueueSnackbar(error.response?.data?.error || 'Login failed', {variant: 'error'})
    } finally {
      setIsLoading(false)
    }
  }

  const handleForgotPassword = () => {
    const {username} = getValues()
    const url = username ? `/forgot-password?username=${encodeURIComponent(username)}` : '/forgot-password'
    navigate(url)
  }

  const renderPasswordField = () => (
    <Grid item xs={12}>
      <StyledTextField
        name='password'
        label='Password'
        variant='outlined'
        type={showPassword ? 'text' : 'password'}
        fullWidth
        InputProps={{
          endAdornment: (
            <PasswordToggleButton onClick={() => setShowPassword((v) => !v)} edge='end'>
              {showPassword ? <VisibilityIcon /> : <VisibilityOffIcon />}
            </PasswordToggleButton>
          ),
        }}
      />
    </Grid>
  )

  const renderLoginButtons = () => (
    <Grid item xs={12}>
      <Stack spacing={2}>
        <StyledLoadingButton
          loading={isLoading}
          type='submit'
          startIcon={isPhoneNumber ? <PhoneIcon /> : <LoginIcon />}
          variant='contained'
          fullWidth
          size='large'
        >
          {isPhoneNumber ? 'Send OTP' : 'Sign In'}
        </StyledLoadingButton>

        {!isPhoneNumber && !isProzoEmail && (
          <ForgotPasswordButton onClick={handleForgotPassword} variant='text'>
            Forgot your password?
          </ForgotPasswordButton>
        )}
      </Stack>
    </Grid>
  )

  const renderGoogleSignIn = () => (
    <Grid item xs={12}>
      <GoogleSignInButton
        onClick={handleGoogleSignIn}
        variant='outlined'
        fullWidth
        size='large'
        startIcon={<GoogleIcon />}
      >
        Continue with Google
      </GoogleSignInButton>
    </Grid>
  )
  const renderMSALSignIn = () => (
    <Grid item xs={12}>
      <MSALSignInButton
        onClick={handleMSALSignIn}
        variant='outlined'
        fullWidth
        size='large'
        startIcon={<WindowIcon />}
      >
        Continue with Microsoft
      </MSALSignInButton>
    </Grid>
  )

  const showSocialSection = true

  const showPrimaryForm = environment !== 'prod'
    ? true
    : !isProzoEmail

  return (
    <Box>

      {/* ── Primary login: Email / Phone ── */}
      <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <StyledTextField
              autoFocus
              name='username'
              label='Email or Phone Number'
              variant='outlined'
              placeholder='Enter your email or 10-digit phone'
              fullWidth
              InputProps={{
                startAdornment: (
                  <IconContainer>
                    <StyledIcon>{isEmail ? <EmailIcon /> : <PhoneIcon />}</StyledIcon>
                  </IconContainer>
                ),
              }}
            />
          </Grid>

          {showPrimaryForm && (
            <>
              {isEmail && renderPasswordField()}
              {renderLoginButtons()}
            </>
          )}
        </Grid>
      </FormProvider>

      {/* ── Divider: "or continue with" ── */}
      {(showSocialSection || (!isProzoEmail && environment === 'prod')) && (
        <Box sx={{display: 'flex', alignItems: 'center', my: 2.5}}>
          <Box sx={{flex: 1, height: '1px', bgcolor: '#e5e7eb'}} />
          <Typography sx={{px: 2, fontSize: '0.75rem', color: '#9ca3af', fontFamily: 'Poppins, sans-serif', whiteSpace: 'nowrap'}}>
            or continue with
          </Typography>
          <Box sx={{flex: 1, height: '1px', bgcolor: '#e5e7eb'}} />
        </Box>
      )}

      {/* ── Social login buttons — side by side ── */}
      <Box sx={{display: 'flex', gap: 1.5}}>
        {(environment !== 'prod' || !isProzoEmail) && (
          <Box onClick={handleMSALSignIn} sx={{
            flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1,
            py: 1.25, borderRadius: '10px', cursor: 'pointer',
            border: '1.5px solid #e5e7eb', bgcolor: '#fff',
            fontFamily: 'Poppins, sans-serif', fontSize: '0.82rem', fontWeight: 600, color: '#0d1b2a',
            transition: 'all 0.15s',
            '&:hover': {borderColor: '#3783e7', bgcolor: '#f8faff', boxShadow: '0 2px 8px rgba(55,131,231,0.1)'},
          }}>
            <WindowIcon sx={{fontSize: 18, color: '#0078d4'}} />
            Microsoft
          </Box>
        )}
        <Box onClick={handleGoogleSignIn} sx={{
            flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1,
            py: 1.25, borderRadius: '10px', cursor: 'pointer',
            border: '1.5px solid #e5e7eb', bgcolor: '#fff',
            fontFamily: 'Poppins, sans-serif', fontSize: '0.82rem', fontWeight: 600, color: '#0d1b2a',
            transition: 'all 0.15s',
            '&:hover': {borderColor: '#ea4335', bgcolor: '#fff8f7', boxShadow: '0 2px 8px rgba(234,67,53,0.1)'},
          }}>
            <GoogleIcon sx={{fontSize: 18, color: '#ea4335'}} />
            Google
          </Box>
      </Box>
    </Box>
  )
}
